import { authAxios } from '@libraries/common/axios';
import { setAccessToken } from '@libraries/auth';
import type { SteadioTermsValue } from '@steadioTypes/terms';
import type {
  SocialAuthResponse,
  SocialLinkRequest,
  SocialSignInRequest,
  SocialSignUpRequest,
} from '@steadioTypes/auth';

export type SignUpRequest = {
  [T in SteadioTermsValue]: boolean;
} & {
  email: string;
  userName: string;
  password: string;
};

export const signUp = async (payload: SignUpRequest): Promise<boolean> => {
  await authAxios.post('/api/v1/auth/signup', payload);
  return true;
};

export type SignInRequest = {
  email: string;
  password: string;
};

type SignInWithTokenRequest = {
  token: string;
};

export const signIn = async (payload: SignInRequest): Promise<string> => {
  const { data } = await authAxios.post('/api/v1/auth/signin', payload);
  return data.body.result.accessToken;
};

export const signInWithToken = async (
  payload: SignInWithTokenRequest,
): Promise<string> => {
  const { data } = await authAxios.post('/api/v1/auth/signin/token', payload);
  return data.body.result.accessToken;
};

export const signOut = async (): Promise<boolean> => {
  await authAxios.post('/api/v1/auth/signout');

  setAccessToken('');

  return true;
};

export const refreshTokens = async (): Promise<boolean> => {
  const response = await authAxios.post('/api/v1/auth/user/token/refresh');
  const { body } = response.data;
  const { error, errorCode, errorMessage, result } = body;

  if (error) {
    throw { errorCode, errorMessage };
  }

  const { accessToken } = result;
  setAccessToken(accessToken);

  return true;
};

export const requestEmailVerificationMail = async (
  email: string,
): Promise<boolean> => {
  const { data } = await authAxios.post('/api/v1/user/verify/email', { email });
  const { body } = data;
  const { result } = body;
  const { requested } = result;

  return requested as boolean;
};

export const requestPasswordResetMail = async (
  email: string,
): Promise<boolean> => {
  const { data } = await authAxios.post('/api/v1/auth/find-password', {
    email,
  });
  const { body } = data;
  const { result } = body;
  const { requested } = result;

  return requested as boolean;
};

export const resetPassword = async (payload: {
  token: string;
  password: string;
}): Promise<boolean> => {
  const { data } = await authAxios.put('/api/v1/auth/reset-password', payload);
  const { body } = data;
  const { result } = body;

  return result as boolean;
};

export const socialSignIn = async (
  payload: SocialSignInRequest,
): Promise<SocialAuthResponse> => {
  const { data } = await authAxios.post('/api/v1/auth/signin/social', payload);
  return data.body.result as SocialAuthResponse;
};

export const socialSignUp = async (
  payload: SocialSignUpRequest,
): Promise<SocialAuthResponse> => {
  const { data } = await authAxios.post('/api/v1/auth/signup/social', payload);
  return data.body.result as SocialAuthResponse;
};

export const socialLink = async (
  payload: SocialLinkRequest,
): Promise<SocialAuthResponse> => {
  const { data } = await authAxios.post('/api/v1/auth/social-link', payload);
  return data.body.result as SocialAuthResponse;
};
